<template>
  <v-dialog
    v-model="showingDialog"
    persistent
    max-height="680"
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >

    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="list-users-actions-updateUser-activator">
        <slot></slot>
      </div>
    </template>

    <v-form
      ref="form"
      v-on:submit.prevent="updatePassword"
      v-bind:disabled="isFormDisabled"
      autocomplete="off"
      data-cy="dialog-updatePassword-form">

      <v-card v-cloak data-cy="dialog-updatePassword-modal">
        <v-card-title class="d-flex justify-space-between mb-2">
          {{$t('routes.users.changeUserPassword')}}

          <v-btn icon @click="closeDialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pt-4 scrollable" data-cy="dialog------">
          <div>
            <v-text-field
              type="password"
              v-bind:label="$t('menus.createUser.password')"
              v-model="password"
              v-bind:rules="passwordRules"
              autocomplete="new-password"
              v-bind:required="password || repassword"
              block
              variant="solo-filled" flat
              data-cy="dialog-updatePassword-password">
            </v-text-field>
          </div>

          <div>
            <v-text-field
              type="password"
              v-bind:label="$t('menus.createUser.repassword')"
              v-model="repassword"
              v-bind:rules="[passwordConfirmationRule]"
              autocomplete="new-password"
              v-bind:required="repassword || password"
              block
              variant="solo-filled" flat
              data-cy="dialog-updatePassword-rePassword">
            </v-text-field>
          </div>

          <div class="bg-error" v-if="errorMessage" data-cy="dialog-updatePassword-apiError">
            {{errorMessage}}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            v-bind:disabled="isFormDisabled"
            type="submit"
            class="bg-primary"
            elevation=0
            data-cy="dialog-updatePassword-submitButton"
          >
            {{$t('routes.users.updatePassword')}}
          </v-btn>

          <v-btn variant="text" @click="closeDialog()" data-cy="dialog-updatePassword-closeButton">
            {{$t('common.cancel')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useUserStore } from '@/stores/UserStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import Helper from '../../../../../components/helper.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  emits: ['update:modelValue'],
  name: "UpdatePasswordDialog",
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    getEditUser: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      errorMessage: "",

      password: "",
      passwordRules: Helper.getPasswordRules(this, { required: true, minLength: true, specialChars: true }),

      repassword: "",
    };
  },
  mounted: function() {
    this.resetForm();
  },
  watch: {
    modelValue() {
      this.resetForm();
    },
    getEditUser: function() {
      this.resetForm();
    }
  },
  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.$nextTick(() => {
        this.errorMessage = "";

        this.password = "";
        this.repassword = "";
      });
    },
    closeDialog() {
      this.showingDialog = false;
    },
    async updatePassword() {
      this.errorMessage = "";

      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      let formData = {
        password: this.password,
        userId: this.getEditUser.id,
        nodeId: this.currentNode.id,
      };

      try {
        const userStore = useUserStore();
        await userStore.updatePassword(formData);

        this.$showMessage(this.$t('routes.users.passwordUpdatedSuccessfully'));
        this.closeDialog();
      } catch (error) {
        console.error(error);

        const apiError = error?.response?.data?.error?.message;
        if (apiError) {
          return this.errorMessage = apiError;
        }
      }
    },
    checkForOneMainTenant: function(user) {
      if (Array.isArray(user?.tenants) && user.tenants.length === 1) {
        const tenantId = user.tenants[0].id;

        if (this.activeTenantId === tenantId) {
          return true;
        }
      }

      return false;
    },
  },
  computed: {
    passwordConfirmationRule() {
      return this.password === this.repassword || this.$t('menus.createUser.passwordsNotMatch');
    },
    currentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    activeTenantId: function() {
      const authStore = useAuthStore();
      return authStore.getActiveTenantId;
    },
    canChangeUserPassword: function () {
      const hasOneMainTenant = this.checkForOneMainTenant(this.getEditUser);

      return this.$canControlUser() && hasOneMainTenant;
    },
    isFormDisabled: function() {
      return !this.getEditUser || !this.currentNode || !this.canChangeUserPassword;
    },
    showingDialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    }
  },
}
</script>

<style>
</style>
